import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Reply, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { notesApi } from '../../lib/api';
import { getActingOnBehalfOf } from '../../lib/sharedAccess';
import { formatDate } from '../../lib/utils';
import { STANCES } from './NotesThread';

const stanceShown = (id) => STANCES.find((s) => s.id === id)?.shown;

/** A small icon-only action under a comment: labelled for screen readers, with a tooltip. */
const IconAction = ({ label, onClick, disabled, danger, testId, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} aria-label={label} title={label} data-testid={testId}
    className={`inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-55 hover:bg-inset transition-colors duration-state disabled:opacity-50 ${danger ? 'hover:text-breach-ink' : 'hover:text-ink'}`}>
    {children}
  </button>
);

/** Who's looking: the account holder, or someone they share with. */
const viewer = () => getActingOnBehalfOf();

const wroteIt = (n, acting) => (acting
  ? !!n.by_invitee && (!n.by_invitation_id || n.by_invitation_id === acting.invitation_id)
  : !n.by_invitee);

const authorOf = (n, acting) => {
  if (wroteIt(n, acting)) return 'You';
  if (n.by_invitee) return n.author_name || 'Someone you share with';
  return acting?.owner_name || 'The account holder';
};

/**
 * Comments on something a record says — a record or a highlighted passage —
 * with replies underneath each, so the person and whoever they share with
 * can go back and forth. "Add a comment" sits above the thread; a reply is
 * always to the comment that started it (one level, services/notes.py).
 * Only the writer can edit a comment; the account holder can delete any.
 *
 * `comments` are the top-level ones, already loaded by the page (it loads a
 * whole collection's or record's in one go); replies are loaded here.
 * `orgId` files a new comment under an organisation — needed for a
 * finding, whose id is only unique within one.
 * `tone` is the bubble colour: ground on a white panel, surface on ground.
 * `compact` is for a thread nested inside something else's box (a saved
 * item in Collections): tighter bubbles and no header of its own — the
 * item's own comment button opens the composer, via `composerOpen` /
 * `onComposerChange`.
 */
export const CommentThread = ({
  targetType, targetId, orgId, comments, onChanged, withStance = true, heading, prompt,
  tone = 'ground', compact = false, composerOpen, onComposerChange, testId = 'comment-thread',
}) => {
  const acting = viewer();
  const readOnly = !!acting && acting.permission !== 'read_write';
  const top = useMemo(() => (comments || []).filter((n) => n.kind === 'comment'), [comments]);
  const [replies, setReplies] = useState({});
  const [composing, setComposing] = useState(null); // null | 'new' | <comment id being replied to>
  const [draft, setDraft] = useState('');
  const [stance, setStance] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const bubble = `${tone === 'surface' ? 'bg-surface' : 'bg-ground'} ${compact ? 'p-3 space-y-1' : 'p-3.5 space-y-1.5'}`;
  const bodyText = compact ? 'text-[15px]' : 'text-[16px]';

  const topKey = top.map((n) => n.id).join('|');
  const loadReplies = useCallback(async () => {
    const ids = topKey ? topKey.split('|') : [];
    if (!ids.length) { setReplies({}); return; }
    try {
      const list = (await notesApi.list({ kind: 'comment', target_type: 'comment', target_ids: ids })).data?.notes || [];
      const by = {};
      list.filter((n) => n.target_type === 'comment').forEach((n) => { (by[n.target_id] = by[n.target_id] || []).push(n); });
      setReplies(by);
    } catch {
      setReplies({});
    }
  }, [topKey]);
  useEffect(() => { loadReplies(); }, [loadReplies]);

  // The new-comment box can be opened from outside (an item's comment button).
  const controlled = composerOpen !== undefined;
  useEffect(() => {
    if (controlled) setComposing((c) => (composerOpen ? 'new' : (c === 'new' ? null : c)));
  }, [controlled, composerOpen]);
  const reset = () => {
    setComposing(null); setDraft(''); setStance(null); setError(null);
    if (controlled) onComposerChange?.(false);
  };

  const run = async (fn, after) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      after();
    } catch (e) {
      setError(e?.response?.data?.detail || 'That didn’t save — try again.');
    } finally {
      setBusy(false);
    }
  };

  const post = () => {
    if (!draft.trim()) return;
    const replyTo = composing !== 'new' ? composing : null;
    run(() => notesApi.create({
      kind: 'comment',
      target_type: replyTo ? 'comment' : targetType,
      target_id: replyTo || targetId,
      // A finding's id is only unique within its organisation.
      ...(!replyTo && orgId ? { org_id: orgId } : {}),
      text: draft.trim(),
      stance: !replyTo && withStance ? stance : null,
    }), () => { reset(); if (replyTo) loadReplies(); else onChanged?.(); });
  };

  const saveEdit = (isReply) => {
    if (!editing?.text?.trim()) return;
    run(() => notesApi.update(editing.id, { text: editing.text.trim(), stance: editing.stance || null }),
      () => { setEditing(null); if (isReply) loadReplies(); else onChanged?.(); });
  };

  const remove = (n, isReply) => run(() => notesApi.remove(n.id), () => { if (isReply) loadReplies(); else { onChanged?.(); loadReplies(); } });

  const StancePicker = ({ value, onChange }) => (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="What kind of comment is this?">
      {STANCES.map((s) => (
        <button key={s.id} type="button" role="radio" aria-checked={value === s.id} onClick={() => onChange(value === s.id ? null : s.id)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium border ${value === s.id ? 'bg-brand-wash border-brand text-brand-ink' : 'bg-surface border-rule text-ink-70 hover:bg-inset'}`}>
          {s.label}
        </button>
      ))}
    </div>
  );

  const composer = (isReply) => (
    <div className="space-y-2" data-testid={`${testId}-composer`}>
      {!isReply && withStance && <StancePicker value={stance} onChange={setStance} />}
      <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus
        placeholder={isReply ? 'Write a reply…' : 'Write it in your own words…'}
        aria-label={isReply ? 'Your reply' : 'Your comment'} className="min-h-[72px] bg-surface" data-testid={`${testId}-input`} />
      {error && <p className="text-sm text-breach-ink">{error}</p>}
      <div className="flex gap-2">
        <Button size="sm" onClick={post} disabled={busy || !draft.trim()} data-testid={`${testId}-post`}>{isReply ? 'Reply' : 'Post'}</Button>
        <Button size="sm" variant="ghost" onClick={reset}>Cancel</Button>
      </div>
    </div>
  );

  const renderComment = (n, isReply) => {
    const mine = wroteIt(n, acting);
    const canDelete = !readOnly && (mine || !acting);
    return (
      <li key={n.id} className={`rounded-lg ${bubble}`} data-testid={isReply ? `${testId}-reply` : `${testId}-comment`}>
        {editing?.id === n.id ? (
          <div className="space-y-2">
            {!isReply && withStance && <StancePicker value={editing.stance} onChange={(v) => setEditing({ ...editing, stance: v })} />}
            <Textarea value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} aria-label="Your comment" className="bg-surface" />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => saveEdit(isReply)} disabled={busy}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-ink-55">
              <span className="font-semibold text-ink">{authorOf(n, acting)}</span>
              <span aria-hidden="true"> · </span>{formatDate(n.created_at)}
              {stanceShown(n.stance) && <><span aria-hidden="true"> · </span>{stanceShown(n.stance)}</>}
            </p>
            <p className={`${bodyText} text-ink leading-relaxed whitespace-pre-wrap`}>{n.text}</p>
            {!readOnly && (
              <div className="flex gap-1 -ml-1.5">
                {!isReply && (
                  <IconAction label="Reply" onClick={() => { reset(); setComposing(n.id); }} testId={`${testId}-reply-btn`}>
                    <Reply className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />
                  </IconAction>
                )}
                {mine && (
                  <IconAction label="Edit" onClick={() => setEditing({ ...n })} testId={`${testId}-edit-btn`}>
                    <Pencil className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />
                  </IconAction>
                )}
                {canDelete && (
                  <IconAction label="Delete" onClick={() => remove(n, isReply)} disabled={busy} danger testId={`${testId}-delete-btn`}>
                    <Trash2 className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />
                  </IconAction>
                )}
              </div>
            )}
          </>
        )}
      </li>
    );
  };

  return (
    <section className={compact ? 'space-y-2' : 'space-y-3'} data-testid={testId}>
      {!compact && (heading || (!readOnly && composing !== 'new')) && (
        <div className="flex items-center justify-between gap-3">
          {heading ? <h3 className="text-[15px] font-semibold text-ink">{heading}</h3> : <span />}
          {!readOnly && composing !== 'new' && (
            <Button size="sm" variant="secondary" onClick={() => { reset(); setComposing('new'); }} data-testid={`${testId}-add`}>
              Add a comment
            </Button>
          )}
        </div>
      )}
      {composing === 'new' && composer(false)}
      {prompt && top.length === 0 && composing !== 'new' && <p className="text-[15px] text-ink-55 leading-relaxed">{prompt}</p>}

      {top.length > 0 && (
        <ul className={compact ? 'space-y-2' : 'space-y-3'}>
          {top.map((n) => (
            <li key={n.id} className="space-y-2">
              <ul>{renderComment(n, false)}</ul>
              {((replies[n.id] || []).length > 0 || composing === n.id) && (
                <div className="ml-5 sm:ml-8 pl-4 border-l-2 border-rule space-y-2">
                  {(replies[n.id] || []).length > 0 && <ul className="space-y-2">{replies[n.id].map((r) => renderComment(r, true))}</ul>}
                  {composing === n.id && composer(true)}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default CommentThread;
